import type { SocialListeningCompanyInput } from "src/services/SocialListeningAPIs";
import type { AnalysisType, BrandFormRow, LoaderState } from "../Types/SocialListeningTypes";
import { asRecord } from "./JsonUtils";

const normalizeHandle = (value?: string) => (value || "").trim().replace(/^@+/, "");

const normalizeFacebookPageId = (value?: string) => {
  const input = normalizeHandle(value);
  if (!input) {return "";}

  const withoutProtocol = input.replace(/^https?:\/\//i, "").replace(/^www\./i, "");
  const facebookHostMatch = withoutProtocol.match(/^(?:m\.|web\.)?facebook\.com\/(.+)$/i);
  const pagePath = facebookHostMatch?.[1] || withoutProtocol;
  const cleanPath = pagePath.split(/[?#]/)[0].replace(/^pages\//i, "");
  return cleanPath.split("/").filter(Boolean).pop() || "";
};

const parseSubreddits = (value?: string) =>
  (value || "")
    .split(/[\n,]+/)
    .map(item =>
      item
        .trim()
        .replace(/^https?:\/\/(?:www\.)?reddit\.com\/r\//i, "")
        .replace(/^\/?r\//i, "")
        .replace(/^@+/, "")
        .split(/[/?#]/)[0]
    )
    .filter(Boolean);

export const formRowToCompany = (row: BrandFormRow): SocialListeningCompanyInput => {
  const company: SocialListeningCompanyInput = { company_name: (row.brandName || "").trim() };

  const facebook_handle = normalizeFacebookPageId(row.facebook);
  const instagram_handle = normalizeHandle(row.instagram);
  const tiktok_brand = normalizeHandle(row.tiktok);
  const reddit_additional_subreddits = parseSubreddits(row.redditSubreddits);

  if (facebook_handle) {company.facebook_handle = facebook_handle;}
  if (instagram_handle) {company.instagram_handle = instagram_handle;}
  if (tiktok_brand) {company.tiktok_brand = tiktok_brand;}
  if (reddit_additional_subreddits.length > 0) {
    company.reddit_additional_subreddits = reddit_additional_subreddits;
  }

  return company;
};

const optionalText = (value: unknown) => (typeof value === "string" ? value : undefined);

const parseBrandRow = (value: unknown): BrandFormRow => {
  const row = asRecord(value);
  return {
    brandName: optionalText(row?.brandName),
    instagram: optionalText(row?.instagram),
    facebook: optionalText(row?.facebook),
    tiktok: optionalText(row?.tiktok),
    redditSubreddits: optionalText(row?.redditSubreddits),
  };
};

const parseAnalysisType = (value: unknown): AnalysisType | undefined =>
  value === "brand_analysis" || value === "competitive_analysis" ? value : undefined;

export const parseLoaderState = (raw: unknown): LoaderState => {
  const state = asRecord(raw);
  const brand = state?.brand;
  const competitors = state?.competitors;
  return {
    analysisType: parseAnalysisType(state?.analysisType),
    brand: brand === undefined ? undefined : parseBrandRow(brand),
    competitors: Array.isArray(competitors) ? competitors.map(parseBrandRow) : undefined,
  };
};
